import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Main from '../../../component/Main';
import SearchBtn from '../../../component/common/button/SearchBtn';
import NewDataGrid from '../../../component/common/dataGrid/NewDataGrid';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import { useModalOpen } from '../../../hooks/useToastModalOpen';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import useMenuPermission from '../../../hooks/useMenuPermission';
import * as Icons from '../../../images/icons/icons_component';
import RoleMenuTree from './RoleMenuTree';
import { buildMenuTree, collectGranted, countAll, isSameAuthMap } from './roleMenuTreeUtil';
import { ROLE_API, handleRoleApiError, roleGridPropsInfo, roleSearchParamsInit, roleSelectCellParams } from './roleMngApi';

import '../../../css/role-mng.css';

const emptyTree = { systems: [], authMap: {} };

/**
 * 롤별메뉴접근관리 (/manager/role_menu_mng.do)
 * 상단 롤 목록에서 롤 선택 → 하단 메뉴 트리에서 조회 · 등록·수정 · 삭제 체크 → 저장 (EMSINFO_AUTH 반영)
 */
const RoleMenuMng = () => {

    const [searchParams, setSearchParams] = useState(roleSearchParamsInit);
    const searchParamsAdditional = {};

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const [state, setState] = useState({
        dataList: [],
        isSearched: false,
        isLoading: false,
    });

    const [pageInfo, setPageInfo] = useState({
        paginationTF: false,
        currentPage: 1,
        itemsPerPage: 50,
        totalItems: 0,
    });

    const [reSearch, setReSearch] = useState(false);

    // 미사용 메뉴는 개발자 롤(110)만 본다. 서버가 세션으로 정해 트리와 함께 showUnused 로 준다.
    // 110 도 '미사용 메뉴 제외' 를 체크하면 사용 메뉴만 본다(숨긴 메뉴의 권한은 저장할 때 그대로 남는다)
    const [canSeeUnused, setCanSeeUnused] = useState(false);
    const [excludeUnused, setExcludeUnused] = useState(false);
    const showUnused = canSeeUnused && !excludeUnused;

    const showNotiModal = useModalStore(state => state.showNotiModal);
    const confirm = useConfirm();
    const { toastModalOpen } = useModalOpen();

    // 권한코드 목록 (검색조건)
    const [authList, setAuthList] = useState([]);
    useEffect(() => {
        const fetchAuthList = async () => {
            try {
                const response = await axios.get(ROLE_API.authCodes);
                setAuthList(response.data.authList ?? []);
            } catch (error) {
                handleRoleApiError(error, showNotiModal, '권한코드 목록 조회 중 오류가 발생했습니다.');
            }
        };
        fetchAuthList();
    }, [showNotiModal]);

    // 선택 롤 + 메뉴 트리
    const [selectRole, setSelectRole] = useState(null);
    const [selectRow, setSelectRow] = useState();
    const [tree, setTree] = useState(emptyTree);
    const [authMap, setAuthMap] = useState({});
    const [treeLoading, setTreeLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const dirty = useMemo(() => !isSameAuthMap(tree.authMap, authMap), [tree.authMap, authMap]);
    const counts = useMemo(() => countAll(authMap, tree.systems), [authMap, tree.systems]);

    const loadTree = async (role) => {
        setTreeLoading(true);
        try {
            const response = await axios.get(ROLE_API.menuTree, { params: { authjobs: role.authjobs, roleno: role.roleno } });
            const built = buildMenuTree(response.data.menuList ?? []);
            setCanSeeUnused(response.data.showUnused === true);
            setTree(built);
            setAuthMap(built.authMap);
        } catch (error) {
            setTree(emptyTree);
            setAuthMap({});
            handleRoleApiError(error, showNotiModal, '메뉴 접근권한 조회 중 오류가 발생했습니다.');
        } finally {
            setTreeLoading(false);
        }
    };

    // 그리드 행 선택 (저장하지 않은 변경이 있으면 확인)
    const handleOnClick = async (dataList, index, selectCellParams, key) => {
        const row = dataList[index];
        const role = {
            roleno: row[selectCellParams[0]],
            rolename: row[selectCellParams[1]],
            authjobs: row[selectCellParams[2]],
            authname: row[selectCellParams[3]],
        };
        if (selectRole && selectRole.roleno === role.roleno) return;
        if (selectRole && dirty) {
            const ok = await confirm('저장하지 않은 변경사항이 있습니다.\n다른 롤을 선택하면 변경사항이 사라집니다. 계속하시겠습니까?');
            if (!ok) return;
        }
        setSelectRole(role);
        setSelectRow(key);
        loadTree(role);
    };

    // 브라우저 이탈 시 미저장 경고
    useEffect(() => {
        if (!dirty) return undefined;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [dirty]);

    const save = async () => {
        if (!selectRole) {
            showNotiModal('롤을 선택해주세요.');
            return;
        }
        if (!dirty) {
            showNotiModal('변경사항이 없습니다.');
            return;
        }
        const ok = await confirm(`[${selectRole.rolename}] 롤의 메뉴 접근권한을 저장하시겠습니까?\n(부여 ${counts.granted}건 / 전체 ${counts.total}건${canSeeUnused && counts.unusedGranted > 0 ? `, 미사용 메뉴 부여 ${counts.unusedGranted}건` : ''})`);
        if (!ok) return;

        try {
            setSaving(true);
            const response = await axios.post(ROLE_API.saveMenuAuth, {
                authjobs: selectRole.authjobs,
                roleno: selectRole.roleno,
                authList: collectGranted(authMap),
            });
            const { result, resultMessage } = response.data.resultMap;
            if (result) {
                toastModalOpen(resultMessage);
                loadTree(selectRole);
            } else {
                showNotiModal(resultMessage);
            }
        } catch (error) {
            handleRoleApiError(error, showNotiModal, '메뉴 접근권한 저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const reset = () => setAuthMap(tree.authMap);

    const activeEnter = (e) => {
        if (e.key === 'Enter') setReSearch(true);
    };

    const { primaryBackColor } = useDynamicStyle();
    // 롤별메뉴접근관리 메뉴 권한(롤 권한 + 개인 권한). 버튼은 그대로 두고 권한이 없으면 누를 때 안내한다(서버도 같은 기준으로 막는다)
    const { withPermission } = useMenuPermission();

    return (
        <Main>
            <div className='h-full flex flex-col min-h-0 flex-grow'>
                <div className='com-search'>
                    <table>
                        <colgroup>
                            <col width='12%' />
                            <col width='21%' />
                            <col width='12%' />
                            <col width='21%' />
                            <col width='12%' />
                            <col width='22%' />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th><label htmlFor='role-menu-search-authjobs'>권한코드</label></th>
                                <td>
                                    <select id='role-menu-search-authjobs' className='w-full' name='searchAuthJobs' value={searchParams.searchAuthJobs} onChange={handleInputChange}>
                                        <option value=''>==전체==</option>
                                        {authList.map((item) => (
                                            <option key={item.bscdminr} value={item.bscdminr}>{item.bscdminr} - {item.bscddesc}</option>
                                        ))}
                                    </select>
                                </td>
                                <th><label htmlFor='role-menu-search-rolename'>롤명</label></th>
                                <td>
                                    <input id='role-menu-search-rolename' type='text' className='input_style' name='searchRoleName'
                                        value={searchParams.searchRoleName} onChange={handleInputChange} onKeyDown={activeEnter} />
                                </td>
                                <th><label htmlFor='role-menu-search-useyn'>사용여부</label></th>
                                <td>
                                    <select id='role-menu-search-useyn' className='w-full' name='searchUseYn' value={searchParams.searchUseYn} onChange={handleInputChange}>
                                        <option value=''>==전체==</option>
                                        <option value='Y'>사용</option>
                                        <option value='N'>미사용</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <SearchBtn
                    propsInfo={roleGridPropsInfo}
                    state={state}
                    setState={setState}
                    searchParams={searchParams}
                    searchParamsAdditional={searchParamsAdditional}
                    pageInfo={pageInfo}
                    setPageInfo={setPageInfo}
                    reSearch={reSearch}
                    setReSearch={setReSearch}
                />
                <div className='flex flex-col min-h-0 flex-grow overflow-auto pr-1'>
                    <div className='role_split_top'>
                        <div className='mb-2 bold'>■ 롤 목록</div>
                        <div className='flex overflow-hidden flex-grow mng_data_grid'>
                            <NewDataGrid
                                propsInfo={roleGridPropsInfo}
                                state={state}
                                pageInfo={pageInfo}
                                setPageInfo={setPageInfo}
                                selectCellParams={roleSelectCellParams}
                                handleOnClick={handleOnClick}
                                selectRow={selectRow}
                            />
                        </div>
                    </div>
                    <div className='role_split_bottom'>
                        <div className='role_tree_toolbar'>
                            <div className='bold'>
                                ■ 메뉴 접근권한
                                {selectRole &&
                                    <span className='role_selected'>&nbsp;— {selectRole.authname} / {selectRole.rolename} ({selectRole.roleno})</span>
                                }
                                {selectRole &&
                                    <span className='role_tree_count'>
                                        부여 {counts.granted} / 전체 {counts.total}
                                        {showUnused && counts.unusedTotal > 0 && ` · 미사용 부여 ${counts.unusedGranted} / ${counts.unusedTotal}`}
                                    </span>
                                }
                                {dirty && <span className='role_dirty_badge'>&nbsp;● 저장되지 않은 변경</span>}
                            </div>
                            <div className='flex gap-1'>
                                {canSeeUnused &&
                                    <label className='role_unused_toggle' title='체크하면 사용 중인 메뉴만 보입니다'>
                                        <input type='checkbox' checked={excludeUnused} onChange={(e) => setExcludeUnused(e.target.checked)} />
                                        미사용 메뉴 제외
                                    </label>
                                }
                                <button className='pw_btn bg_co_gray' onClick={reset} disabled={!dirty} title='변경 전으로 되돌리기'>
                                    <Icons.CancelCircle16w />&nbsp;되돌리기
                                </button>
                                <button className={`search_btn ${primaryBackColor}`} onClick={withPermission('UPYN', save)} disabled={!selectRole || treeLoading || saving} title='메뉴 접근권한 저장'>
                                    <Icons.Save16w />저장
                                </button>
                            </div>
                        </div>
                        <div className='flex overflow-hidden flex-grow mng_data_grid'>
                            <RoleMenuTree
                                systems={tree.systems}
                                authMap={authMap}
                                setAuthMap={setAuthMap}
                                loading={treeLoading}
                                selected={!!selectRole}
                                showUnused={showUnused}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Main>
    );
};

export default RoleMenuMng;
